import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Divider,
  Download,
  Drawer,
  DrawerCloseButton,
  DrawerTrigger,
  Flex,
  Heading,
  MagnifyingGlass,
  MultiSelectComboBox,
  Page,
  PageHeader,
  Plus,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  TagGroup,
  Text,
  TextField,
  ThemeProvider,
  ToastProvider,
  Tooltip,
  TooltipTrigger,
  useTheme,
  Sun,
  Moon,
  Warning,
  TagBadge,
  type MultiSelectOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Link as LinkIcon, ArrowLeft, ArrowRight } from '@phosphor-icons/react';

// =============================================================================
// MOCK DATA - Generic example data
// =============================================================================

type MockItem = {
  id: string;
  name: string;
  status: 'active' | 'pending' | 'inactive' | 'error';
  priority: 'high' | 'medium' | 'low';
  category: string;
  lastUpdated: string;
  createdAt: string;
  url: string | null;
  description: string;
};

const MOCK_ITEMS: MockItem[] = [
  {
    id: '1',
    name: 'Dashboard redesign project',
    status: 'active',
    priority: 'high',
    category: 'Design',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    url: 'https://example.com/projects/dashboard',
    description: 'Complete overhaul of the main dashboard interface.',
  },
  {
    id: '2',
    name: 'API performance optimization',
    status: 'pending',
    priority: 'high',
    category: 'Engineering',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    url: 'https://example.com/projects/api',
    description: 'Improve response times for critical endpoints.',
  },
  {
    id: '3',
    name: 'User onboarding flow',
    status: 'active',
    priority: 'medium',
    category: 'Product',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
    url: 'https://example.com/projects/onboarding',
    description: 'Streamline the new user experience.',
  },
  {
    id: '4',
    name: 'Documentation update',
    status: 'inactive',
    priority: 'low',
    category: 'Documentation',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    url: null,
    description: 'Update API documentation with new endpoints.',
  },
  {
    id: '5',
    name: 'Security audit findings',
    status: 'error',
    priority: 'high',
    category: 'Security',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    url: 'https://example.com/security/audit',
    description: 'Critical vulnerabilities identified in authentication.',
  },
  {
    id: '6',
    name: 'Mobile app release',
    status: 'pending',
    priority: 'medium',
    category: 'Engineering',
    lastUpdated: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 21).toISOString(),
    url: 'https://example.com/mobile',
    description: 'Prepare v2.0 for app store submission.',
  },
];

// =============================================================================
// SEMANTIC BADGE VARIANTS - General purpose
// =============================================================================

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';

// Status badges - semantic colors based on meaning
const getStatusVariant = (status: MockItem['status']): BadgeVariant => {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'inactive':
      return 'outline';
    case 'error':
      return 'destructive';
  }
};

// Priority badges - urgency-based colors
const getPriorityVariant = (priority: MockItem['priority']): BadgeVariant => {
  switch (priority) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'warning';
    case 'low':
      return 'secondary';
  }
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function ThemeToggle() {
  const { config, setMode } = useTheme();
  return (
    <Button
      variant="outline"
      size="sm"
      onPress={() => setMode(config.scheme === 'light' ? 'dark' : 'light')}
      iconOnly
      aria-label={`Switch to ${config.scheme === 'light' ? 'dark' : 'light'} mode`}
    >
      {config.scheme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
    </Button>
  );
}

function SpecValue({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" className="text-primary font-mono bg-primary/10 px-1.5 py-0.5 rounded">
      {children}
    </Text>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex align="center" gap="sm">
      <Text size="sm" className="text-muted-foreground min-w-[160px]">
        {label}
      </Text>
      <SpecValue>{value}</SpecValue>
    </Flex>
  );
}

function GuidanceCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-primary/30 pl-4 py-2">
      <Text size="sm" weight="medium" className="text-primary mb-2">
        {title}
      </Text>
      <Text size="sm" className="text-muted-foreground">
        {children}
      </Text>
    </div>
  );
}

// =============================================================================
// TABLE COLUMNS - Demonstrating column hierarchy
// =============================================================================

const demoColumns: ColumnDef<StockFeatures, MockItem>[] = [
  // PRIMARY COLUMN: Name - gets largest flex, most visual weight
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    enableSorting: true,
    cell: ({ row }) => (
      <Text size="sm" weight="medium" className="truncate" title={row.original.name}>
        {row.original.name}
      </Text>
    ),
    meta: { flex: 1, maxWidth: 400 }, // Primary: flex 1, generous maxWidth
  },
  // SECONDARY COLUMNS: Status indicators - small flex, tight maxWidth
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => (
      <TagBadge
        name={row.original.status.toUpperCase()}
        color={badgeVariantToTagColor(getStatusVariant(row.original.status))}
        hideDot
      />
    ),
    meta: { flex: 0.15, maxWidth: 100 },
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: 'Priority',
    enableSorting: true,
    cell: ({ row }) => (
      <TagBadge
        name={row.original.priority.toUpperCase()}
        color={badgeVariantToTagColor(getPriorityVariant(row.original.priority))}
        hideDot
      />
    ),
    meta: { flex: 0.15, maxWidth: 100 },
  },
  // METADATA COLUMNS: Category, dates - medium flex
  {
    id: 'category',
    accessorKey: 'category',
    header: 'Category',
    enableSorting: true,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground truncate">
        {row.original.category}
      </Text>
    ),
    meta: { flex: 0.2, maxWidth: 150 },
  },
  {
    id: 'lastUpdated',
    accessorKey: 'lastUpdated',
    header: 'Updated',
    enableSorting: true,
    cell: ({ row }) => {
      const date = new Date(row.original.lastUpdated);
      return (
        <TooltipTrigger delay={100} offset={8}>
          <Text size="sm" className="text-muted-foreground cursor-default">
            {formatDistanceToNow(date, { addSuffix: true })}
          </Text>
          <Tooltip>{date.toLocaleString()}</Tooltip>
        </TooltipTrigger>
      );
    },
    meta: { flex: 0.2, maxWidth: 120 },
  },
  // ICON COLUMN: Link - enough space for header text, icon preferred over full URL
  {
    id: 'link',
    accessorFn: (row) => row.url ?? '',
    header: 'Link',
    cell: ({ row }) => {
      const url = row.original.url;
      if (!url)
        return (
          <Text size="sm" className="text-muted-foreground">
            —
          </Text>
        );
      return (
        <TooltipTrigger delay={100} offset={8}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
          >
            <LinkIcon size={16} />
          </a>
          <Tooltip>{url}</Tooltip>
        </TooltipTrigger>
      );
    },
    meta: { flex: 0.1, maxWidth: 80 },
  },
];

// =============================================================================
// DRAWER CONTENT - Demonstrating layout patterns
// =============================================================================

type DemoDrawerProps = {
  item: MockItem;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  canNavigate: boolean;
};

function DemoDrawerContent({ item, onNavigatePrev, onNavigateNext, canNavigate }: DemoDrawerProps) {
  return (
    <Box className="h-full flex flex-col bg-background">
      {/* STICKY HEADER */}
      <header className="px-6 py-4 border-b sticky top-0 z-20 bg-background">
        <Flex align="start" justify="between" gap="md" wrap="wrap">
          <div className="space-y-3 min-w-0 flex-1">
            <Flex align="center" gap="sm" wrap>
              <TagBadge
                name={item.status.toUpperCase()}
                color={badgeVariantToTagColor(getStatusVariant(item.status))}
                hideDot
              />
              <TagBadge
                name={item.priority.toUpperCase()}
                color={badgeVariantToTagColor(getPriorityVariant(item.priority))}
                hideDot
              />
              <TagBadge name={item.category} color="gray" hideDot />
            </Flex>
            <Heading level={2} className="text-xl leading-tight">
              {item.name}
            </Heading>
          </div>
          {/* Navigation + Close grouped on right */}
          <Flex align="center" gap="sm" className="shrink-0">
            {canNavigate && (
              <Flex gap="sm">
                <Button variant="outline" onPress={onNavigatePrev} iconOnly aria-label="Previous">
                  <ArrowLeft size={16} />
                </Button>
                <Button variant="outline" onPress={onNavigateNext} iconOnly aria-label="Next">
                  <ArrowRight size={16} />
                </Button>
              </Flex>
            )}
            <DrawerCloseButton />
          </Flex>
        </Flex>
        {/* Contextual breadcrumb info */}
        <Flex align="center" justify="between" gap="sm" wrap className="mt-3">
          <Flex align="center" gap="xs" wrap className="min-w-0">
            <Text size="sm" className="text-muted-foreground">
              {item.category}
            </Text>
            <Text size="sm" className="text-muted-foreground">
              /
            </Text>
            {item.url ? (
              <a
                href={item.url}
                className="text-sm font-medium text-primary truncate max-w-[240px]"
              >
                View project
              </a>
            ) : (
              <Text size="sm" className="text-muted-foreground">
                No link
              </Text>
            )}
          </Flex>
          <Text size="xs" className="text-muted-foreground">
            Created {formatDistanceToNow(new Date(item.createdAt))} ago
            {' · '}
            Updated {formatDistanceToNow(new Date(item.lastUpdated), { addSuffix: true })}
          </Text>
        </Flex>
      </header>

      {/* SCROLLABLE CONTENT WITH TABS */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Tabs className="flex flex-col h-full" defaultSelectedKey="details">
          <div className="px-6 pt-4 pb-3 bg-background">
            <TabList>
              <Tab id="details">Details</Tab>
              <Tab id="activity">Activity</Tab>
              <Tab id="comments">Comments</Tab>
            </TabList>
          </div>
          <TabPanel id="details" className="flex-1 overflow-auto p-0">
            <div className="px-6 py-4 space-y-4">
              <div>
                <Heading level={4} className="text-sm font-semibold mb-2">
                  Description
                </Heading>
                <Text size="sm" className="text-muted-foreground">
                  {item.description}
                </Text>
              </div>
              <div className="border border-dashed border-border rounded-lg p-8 text-center">
                <Text size="sm" className="text-muted-foreground">
                  Additional content area
                </Text>
              </div>
            </div>
          </TabPanel>
          <TabPanel id="activity" className="flex-1 overflow-auto p-0">
            <div className="px-6 py-4">
              <Text size="sm" className="text-muted-foreground">
                Activity timeline would appear here.
              </Text>
            </div>
          </TabPanel>
          <TabPanel id="comments" className="flex-1 overflow-auto p-0">
            <div className="px-6 py-4">
              <Text size="sm" className="text-muted-foreground">
                Comments section would appear here.
              </Text>
            </div>
          </TabPanel>
        </Tabs>
      </div>

      {/* STICKY FOOTER - Primary action on right */}
      <footer className="px-6 py-4 border-t bg-muted/30 sticky bottom-0 z-20">
        <Flex align="center" justify="between" gap="md">
          <Button variant="outline">Secondary Action</Button>
          <Button variant="primary">Primary Action</Button>
        </Flex>
      </footer>
    </Box>
  );
}

// =============================================================================
// FILTER OPTIONS
// =============================================================================

const STATUS_OPTIONS: MultiSelectOption[] = [
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'error', label: 'Error' },
];

const PRIORITY_OPTIONS: MultiSelectOption[] = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

const CATEGORY_OPTIONS: MultiSelectOption[] = [
  { id: 'design', label: 'Design' },
  { id: 'engineering', label: 'Engineering' },
  { id: 'product', label: 'Product' },
  { id: 'security', label: 'Security' },
  { id: 'documentation', label: 'Documentation' },
];

// =============================================================================
// MAIN DEMO CONTENT
// =============================================================================

function TablePatternDemoContent() {
  // Filter state
  const [searchInput, setSearchInput] = React.useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<MultiSelectOption[]>([]);
  const [priorityFilter, setPriorityFilter] = React.useState<MultiSelectOption[]>([]);
  const [categoryFilter, setCategoryFilter] = React.useState<MultiSelectOption[]>([]);

  // Table state
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [page, setPage] = React.useState(1);
  const pageSize = 5;

  // Drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  // Compute filter chips from state
  const filterChips = React.useMemo(() => {
    const chips: Array<{ id: string; label: string }> = [];
    statusFilter.forEach((s) => chips.push({ id: `status:${s.id}`, label: `Status: ${s.label}` }));
    priorityFilter.forEach((p) =>
      chips.push({ id: `priority:${p.id}`, label: `Priority: ${p.label}` })
    );
    categoryFilter.forEach((c) =>
      chips.push({ id: `category:${c.id}`, label: `Category: ${c.label}` })
    );
    return chips;
  }, [statusFilter, priorityFilter, categoryFilter]);

  const filteredItems = React.useMemo(() => {
    let items = [...MOCK_ITEMS];
    if (searchInput.trim()) {
      const query = searchInput.toLowerCase();
      items = items.filter((item) => item.name.toLowerCase().includes(query));
    }
    return items;
  }, [searchInput]);

  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);
  const selectedCount = Object.keys(rowSelection).filter((k) => rowSelection[k]).length;

  const handleFilterRemove = React.useCallback((chipId: string) => {
    const [type, id] = chipId.split(':');
    if (type === 'status') setStatusFilter((prev) => prev.filter((s) => s.id !== id));
    if (type === 'priority') setPriorityFilter((prev) => prev.filter((p) => p.id !== id));
    if (type === 'category') setCategoryFilter((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const handleClearAllFilters = React.useCallback(() => {
    setStatusFilter([]);
    setPriorityFilter([]);
    setCategoryFilter([]);
    setSearchInput('');
  }, []);

  const handleItemClick = React.useCallback(
    (item: MockItem) => {
      const index = filteredItems.findIndex((i) => i.id === item.id);
      setSelectedIndex(index >= 0 ? index : 0);
      setDrawerOpen(true);
    },
    [filteredItems]
  );

  const navigateToItem = React.useCallback(
    (index: number) => {
      let newIndex = index;
      if (newIndex < 0) newIndex = filteredItems.length - 1;
      if (newIndex >= filteredItems.length) newIndex = 0;
      setSelectedIndex(newIndex);
    },
    [filteredItems.length]
  );

  const resultsLabel = `${totalItems} ${totalItems === 1 ? 'result' : 'results'}${filterChips.length > 0 ? ` · ${filterChips.length} ${filterChips.length === 1 ? 'filter' : 'filters'}` : ''}`;
  const selectedItem = filteredItems[selectedIndex] ?? filteredItems[0];

  return (
    <Page>
      <PageHeader
        icon={<Warning size={24} />}
        title="Table + Drawer Pattern Guide"
        description="Design specifications and guidance for list views with detail drawers"
        actions={<ThemeToggle />}
      />

      {/* ===================================================================== */}
      {/* SECTION 1: DESIGN PRINCIPLES */}
      {/* ===================================================================== */}
      <div className="mt-8">
        <Heading level={2} className="text-lg mb-4">
          1. Design Principles
        </Heading>
        <Stack gap="md">
          <GuidanceCard title="Column Hierarchy">
            Tables should have 1-2 primary columns (name, title) that get the largest flex value and
            max-width. Secondary columns (status, badges) use small flex values. Metadata columns
            (dates, categories) use medium flex. All cell text should use the same font size
            (size="sm")—use weight and color for hierarchy, not size. For URLs or links, prefer an
            icon with tooltip over displaying the full URL. Always ensure maxWidth accommodates the
            header text, not just the cell content.
          </GuidanceCard>
          <GuidanceCard title="Filter Design">
            Prefer MultiSelectComboBox for most filter fields to allow multiple selections. Order
            fields by usefulness— most commonly used filters first. Provide a single "Clear all"
            action rather than individual clear buttons. Filter chips should be removable
            individually via the TagGroup.
          </GuidanceCard>
          <GuidanceCard title="Drawer Layout">
            Use sticky header and footer. Header contains status badges, title, navigation, and
            contextual breadcrumb info. Footer has secondary actions on left, primary action on
            right. A simple Button is usually sufficient— use SplitButton only when there are
            genuinely related alternative actions.
          </GuidanceCard>
          <GuidanceCard title="Badge Semantics">
            Choose badge variants based on meaning: destructive for errors/critical, warning for
            caution/pending, success for positive/active states, secondary for neutral info, outline
            for inactive/disabled states.
          </GuidanceCard>
        </Stack>
      </div>

      <Divider className="my-8" />

      {/* ===================================================================== */}
      {/* SECTION 2: INTERACTIVE EXAMPLE */}
      {/* ===================================================================== */}
      <div>
        <Heading level={2} className="text-lg mb-4">
          2. Interactive Example
        </Heading>
        <Text size="sm" className="text-muted-foreground mb-6">
          Click a row to open the drawer. Try the filters to see the full pattern.
        </Text>

        {/* Toolbar */}
        <div className="space-y-4">
          <Flex align="center" gap="sm" wrap justify="between">
            <TextField
              aria-label="Search"
              placeholder="Search by name..."
              value={searchInput}
              onChange={setSearchInput}
              leftIcon={<MagnifyingGlass size={16} />}
              className="w-full max-w-md min-w-[200px]"
            />
            <Flex gap="sm" align="center" className="flex-1 justify-end min-w-[180px]">
              {filterChips.length > 0 && (
                <Button variant="ghost" size="sm" onPress={handleClearAllFilters}>
                  Clear all
                </Button>
              )}
              <DialogTrigger
                isOpen={isFilterModalOpen}
                onOpenChange={setIsFilterModalOpen}
                size="lg"
              >
                <Button
                  variant={filterChips.length > 0 ? 'primary' : 'secondary'}
                  iconLeft={filterChips.length === 0 ? <Plus size={16} /> : undefined}
                >
                  {filterChips.length > 0 ? `Filters (${filterChips.length})` : 'Add filter'}
                </Button>
                <Dialog title="Filters" description="Filter the list by selecting options below.">
                  <DialogContent className="space-y-4">
                    <MultiSelectComboBox
                      label="Status"
                      placeholder="Select statuses..."
                      items={STATUS_OPTIONS}
                      value={statusFilter}
                      onChange={setStatusFilter}
                    />
                    <MultiSelectComboBox
                      label="Priority"
                      placeholder="Select priorities..."
                      items={PRIORITY_OPTIONS}
                      value={priorityFilter}
                      onChange={setPriorityFilter}
                    />
                    <MultiSelectComboBox
                      label="Category"
                      placeholder="Select categories..."
                      items={CATEGORY_OPTIONS}
                      value={categoryFilter}
                      onChange={setCategoryFilter}
                    />
                  </DialogContent>
                  <DialogFooter>
                    <Button variant="outline" onPress={handleClearAllFilters}>
                      Clear all
                    </Button>
                    <Button variant="primary" onPress={() => setIsFilterModalOpen(false)}>
                      Done
                    </Button>
                  </DialogFooter>
                </Dialog>
              </DialogTrigger>
            </Flex>
          </Flex>

          <Flex align="center" justify="between" gap="md" wrap>
            <Flex align="center" gap="sm" wrap className="flex-1 min-w-[200px]">
              {filterChips.length > 0 ? (
                <TagGroup
                  aria-label="Active filters"
                  items={filterChips}
                  onRemove={handleFilterRemove}
                />
              ) : (
                <Text size="sm" className="text-muted-foreground">
                  No filters applied
                </Text>
              )}
            </Flex>
            <Flex align="center" gap="sm">
              <Text size="sm" className="text-muted-foreground">
                {resultsLabel}
              </Text>
              <Button variant="icon" size="sm" iconOnly tooltip="Export data">
                <Download size={16} />
              </Button>
            </Flex>
          </Flex>
        </div>

        {/* Table */}
        <div className="mt-4">
          {selectedCount > 0 && (
            <div className="rounded-md border border-primary/40 bg-primary/5 px-4 py-3 mb-3">
              <Flex align="center" justify="between" gap="sm">
                <Text size="sm">{selectedCount} selected</Text>
                <Flex gap="sm">
                  <Button variant="ghost" size="sm" onPress={() => setRowSelection({})}>
                    Clear
                  </Button>
                  <Button variant="primary" size="sm">
                    Bulk Action
                  </Button>
                </Flex>
              </Flex>
            </div>
          )}
          <DataTable
            columns={demoColumns}
            data={paginatedItems}
            aria-label="Demo table"
            enableRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(item) => item.id}
            serverPagination={{
              page,
              pageSize,
              pageCount: totalPages,
              totalItems,
              onPageChange: setPage,
            }}
            onRowClick={handleItemClick}
          />
        </div>

        <DrawerTrigger isOpen={drawerOpen} onOpenChange={setDrawerOpen}>
          <button style={{ display: 'none' }} />
          <Drawer hideCloseButton>
            {selectedItem && (
              <DemoDrawerContent
                item={selectedItem}
                onNavigatePrev={() => navigateToItem(selectedIndex - 1)}
                onNavigateNext={() => navigateToItem(selectedIndex + 1)}
                canNavigate={filteredItems.length > 1}
              />
            )}
          </Drawer>
        </DrawerTrigger>
      </div>

      <Divider className="my-8" />

      {/* ===================================================================== */}
      {/* SECTION 3: SPECIFICATIONS */}
      {/* ===================================================================== */}
      <div>
        <Heading level={2} className="text-lg mb-6">
          3. Specifications
        </Heading>

        <Stack gap="lg">
          {/* Spacing */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Spacing
              </Heading>
            </CardHeader>
            <CardContent>
              <Stack gap="sm">
                <SpecRow label="Page to toolbar" value="mt-6 (24px)" />
                <SpecRow label="Between toolbar rows" value="space-y-4 (16px)" />
                <SpecRow label="Toolbar to table" value="mt-4 (16px)" />
                <SpecRow label="Drawer header/footer padding" value="px-6 py-4 (24px / 16px)" />
                <SpecRow label="Drawer content padding" value="px-6 py-4" />
                <SpecRow label="Gap between badges" value="gap-sm (8px)" />
                <SpecRow label="Gap between form fields" value="space-y-4 (16px)" />
              </Stack>
            </CardContent>
          </Card>

          {/* Typography */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Typography
              </Heading>
            </CardHeader>
            <CardContent>
              <Stack gap="sm">
                <SpecRow label="Drawer title" value="Heading level={2}, text-xl leading-tight" />
                <SpecRow
                  label="Section headings"
                  value="Heading level={4}, text-sm font-semibold"
                />
                <SpecRow label="All table cell text" value="Text size='sm'" />
                <SpecRow label="Primary cell emphasis" value="weight='medium'" />
                <SpecRow label="Secondary cell de-emphasis" value="text-muted-foreground" />
                <SpecRow label="Filter labels" value="Text size='sm' text-muted-foreground" />
              </Stack>
              <GuidanceCard title="Consistent cell typography">
                All table cells should use the same font size (size="sm"). Use weight="medium" for
                primary columns and text-muted-foreground for secondary/metadata columns to create
                visual hierarchy without inconsistent sizing.
              </GuidanceCard>
            </CardContent>
          </Card>

          {/* Table Columns */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Table Column Guidelines
              </Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <Stack gap="sm">
                <SpecRow label="Primary column (name/title)" value="flex: 1, maxWidth: 300-500px" />
                <SpecRow
                  label="Badge columns (status)"
                  value="flex: 0.1-0.15, maxWidth: 80-120px"
                />
                <SpecRow
                  label="Metadata columns (category)"
                  value="flex: 0.15-0.25, maxWidth: 120-200px"
                />
                <SpecRow label="Date columns" value="flex: 0.15-0.2, maxWidth: 100-140px" />
                <SpecRow label="Icon-only columns (links)" value="flex: 0.1, maxWidth: 70-90px" />
              </Stack>
              <GuidanceCard title="Column width must fit the header">
                Always ensure maxWidth is large enough to display the column header text without
                truncation. Even icon-only columns need enough width for their header label. Test at
                minimum viewport widths.
              </GuidanceCard>
              <GuidanceCard title="When to use icons vs text">
                Use icons for links/URLs (with tooltip showing full URL), boolean indicators, and
                common actions. Use text for data that needs to be scannable or searchable in the
                table.
              </GuidanceCard>
            </CardContent>
          </Card>

          {/* Drawer Structure */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Drawer Structure
              </Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="font-mono text-xs bg-muted p-4 rounded-md overflow-x-auto">
                <pre>{`<Box className="h-full flex flex-col bg-background">
  <header className="px-6 py-4 border-b sticky top-0 z-20 bg-background">
    {/* Badges, title, nav buttons, close */}
    {/* Breadcrumb/context info */}
    {/* Timestamps */}
  </header>
  <div className="flex-1 min-h-0 overflow-hidden">
    <Tabs className="flex flex-col h-full">
      <div className="px-6 pt-4 pb-3">{/* TabList */}</div>
      <TabPanel className="flex-1 overflow-auto p-0">{/* Content */}</TabPanel>
    </Tabs>
  </div>
  <footer className="px-6 py-4 border-t bg-muted/30 sticky bottom-0 z-20">
    <Flex justify="between">
      <Button variant="outline">Secondary</Button>
      <Button variant="primary">Primary</Button>
    </Flex>
  </footer>
</Box>`}</pre>
              </div>
              <Stack gap="sm">
                <SpecRow label="Header" value="sticky top-0 z-20 border-b bg-background" />
                <SpecRow label="Footer" value="sticky bottom-0 z-20 border-t bg-muted/30" />
                <SpecRow label="Content area" value="flex-1 min-h-0 overflow-hidden" />
                <SpecRow label="TabPanel" value="flex-1 overflow-auto p-0" />
              </Stack>
            </CardContent>
          </Card>

          {/* Filter Modal */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Filter Modal
              </Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <Stack gap="sm">
                <SpecRow label="Dialog size" value="size='lg'" />
                <SpecRow label="Field spacing" value="space-y-4 (16px)" />
                <SpecRow label="Preferred field type" value="MultiSelectComboBox" />
                <SpecRow label="Field order" value="Most useful/common filters first" />
              </Stack>
              <GuidanceCard title="Footer actions">
                "Clear all" button (variant="outline") on left clears all filters. "Done" button
                (variant="primary") on right closes the modal. Filters apply immediately as
                selections change.
              </GuidanceCard>
              <GuidanceCard title="Trigger button states">
                No filters: variant="secondary" with Plus icon, text "Add filter". Has filters:
                variant="primary", text "Filters (N)" showing count.
              </GuidanceCard>
            </CardContent>
          </Card>

          {/* Badge Semantics */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Badge Variant Semantics
              </Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Flex align="center" gap="md">
                  <TagBadge name="DESTRUCTIVE" color="red" hideDot />
                  <Text size="sm" className="text-muted-foreground">
                    Errors, critical issues, high priority, danger
                  </Text>
                </Flex>
                <Flex align="center" gap="md">
                  <TagBadge name="WARNING" color="yellow" hideDot />
                  <Text size="sm" className="text-muted-foreground">
                    Caution, pending, needs attention, medium priority
                  </Text>
                </Flex>
                <Flex align="center" gap="md">
                  <TagBadge name="SUCCESS" color="blue" hideDot />
                  <Text size="sm" className="text-muted-foreground">
                    Active, complete, healthy, positive states
                  </Text>
                </Flex>
                <Flex align="center" gap="md">
                  <TagBadge name="SECONDARY" color="gray" hideDot />
                  <Text size="sm" className="text-muted-foreground">
                    Neutral info, low priority, default category
                  </Text>
                </Flex>
                <Flex align="center" gap="md">
                  <TagBadge name="OUTLINE" color="gray" hideDot />
                  <Text size="sm" className="text-muted-foreground">
                    Inactive, disabled, archived, unknown
                  </Text>
                </Flex>
              </div>
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardHeader>
              <Heading level={3} className="text-base">
                Timestamp Display
              </Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border border-border rounded-md p-4 space-y-3">
                <div>
                  <Text size="xs" className="text-muted-foreground font-medium mb-1">
                    Table cells (with tooltip):
                  </Text>
                  <TooltipTrigger delay={100} offset={8}>
                    <Text size="xs" className="text-muted-foreground cursor-default">
                      2 hours ago
                    </Text>
                    <Tooltip>Dec 16, 2024, 10:30 AM</Tooltip>
                  </TooltipTrigger>
                </div>
                <div>
                  <Text size="xs" className="text-muted-foreground font-medium mb-1">
                    Drawer header:
                  </Text>
                  <Text size="xs" className="text-muted-foreground">
                    Created 7 days ago · Updated 2 hours ago
                  </Text>
                </div>
              </div>
              <Stack gap="sm">
                <SpecRow label="Format" value="formatDistanceToNow(date, { addSuffix: true })" />
                <SpecRow label="Tooltip shows" value="Full date/time via toLocaleString()" />
                <SpecRow label="Tooltip delay" value="100ms" />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </div>

      <Divider className="my-8" />

      {/* ===================================================================== */}
      {/* SECTION 4: COMPONENT REFERENCES */}
      {/* ===================================================================== */}
      <div>
        <Heading level={2} className="text-lg mb-4">
          4. Component References
        </Heading>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-semibold">Component</th>
                <th className="text-left py-2 font-semibold">Location</th>
              </tr>
            </thead>
            <tbody className="text-xs">
              <tr className="border-b">
                <td className="py-2 pr-4">DataTable</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Table.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">Drawer, DrawerTrigger, DrawerCloseButton</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Drawer.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">Dialog, DialogTrigger, DialogContent, DialogFooter</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Dialog.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">MultiSelectComboBox</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/MultiSelectComboBox.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">TagGroup</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/TagGroup.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">Badge</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Badge.tsx
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2 pr-4">Tabs, TabList, Tab, TabPanel</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Tabs.tsx
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Page, PageHeader</td>
                <td className="py-2 text-muted-foreground font-mono">
                  packages/ui/src/components/Page.tsx
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Page>
  );
}

export function TablePatternDemo() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <TablePatternDemoContent />
      </ToastProvider>
    </ThemeProvider>
  );
}
